import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { TextInput, SelectDropDown, Button } from '@wot/components';
import summerCampApi from '../../../api/summerCamp';
import { SPINNER } from '../../../assets/animated';
import NoResults from '../../../components/NoResults';
import Pagination from '../../../components/Pagination';
import usePagainate from '../../../hooks/usePaginate';
import TableContainer from './components/Table';

const STATUS_OPTIONS = ['all', 'new', 'contacted', 'qualified', 'converted', 'closed'];

export default function SummerCampEnquiries() {
  const queryClient = useQueryClient();
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage, resetPage } = usePagainate();
  const [sortBy, setSortBy] = useState<{ [key: string]: number }>({ createdAt: -1 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const filters: Record<string, string> = {};
  if (statusFilter !== 'all') filters.status = statusFilter;
  if (debouncedSearch) filters.search = debouncedSearch;

  const queryKey = ['summer-camp-enquiries', currentPage, resultsPerPage, sortBy, statusFilter, debouncedSearch];

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey,
    queryFn: async () => await summerCampApi.get(currentPage, resultsPerPage, filters, { ...sortBy })
  });

  useEffect(() => {
    if (!isPreviousData && data && data.count > resultsPerPage * currentPage) {
      queryClient.prefetchQuery({
        queryKey: ['summer-camp-enquiries', currentPage + 1, resultsPerPage, sortBy, statusFilter, debouncedSearch],
        queryFn: async () => await summerCampApi.get(currentPage + 1, resultsPerPage, filters, { ...sortBy })
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, isError, sortBy, debouncedSearch, statusFilter]);

  useEffect(() => {
    resetPage();
    setSelectedIds(new Set());
  }, [sortBy, statusFilter, debouncedSearch]);

  const handleSort = (key: string) => {
    setSortBy({ [key]: sortBy[key] === 1 ? -1 : 1 });
  };

  const handleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (!data) return;
    if (checked) {
      setSelectedIds(new Set(data.data.map((e) => e._id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} enquiry${selectedIds.size > 1 ? 'ies' : 'y'}?`)) return;
    try {
      await summerCampApi.bulkRemove(Array.from(selectedIds));
      toast.success(`${selectedIds.size} enquiry${selectedIds.size > 1 ? 'ies' : 'y'} deleted`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['summer-camp-enquiries'] });
    } catch {
      toast.error('Failed to delete enquiries');
    }
  };

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow">
      <h1 className="text-xl font-semibold">Summer Camp Enquiries</h1>

      <div className="flex items-center gap-4">
        <div className="w-72">
          <TextInput placeholder="Search by name, email..." value={search} onChange={setSearch} size="small" />
        </div>
        <div className="w-44">
          <SelectDropDown
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
            placeholder="Filter by status"
            size="small"
          />
        </div>
        {selectedIds.size > 0 && (
          <Button
            type="primary"
            text={`Delete (${selectedIds.size})`}
            onClick={handleBulkDelete}
            className="!bg-red-600 !border-red-600 hover:!bg-red-700"
          />
        )}
      </div>

      {isLoading ? (
        <div className="w-full min-h-[256px] flex items-center justify-center">
          <img src={SPINNER} alt="loading" className="w-6 h-6" />
        </div>
      ) : data && data.data.length > 0 ? (
        <>
          <TableContainer
            handleSort={handleSort}
            data={data.data}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onSelectAll={handleSelectAll}
          />
          <Pagination
            count={data.count}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
          />
        </>
      ) : (
        <NoResults />
      )}
    </div>
  );
}
